import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FlaskConical, X } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";

interface DevPanelProps {
  onSimulateChord: () => void;
  onSimulateTransition: () => void;
  onRecovery: (level: number | null) => void;
  onStableFlow: () => void;
}

// Floating simulation deck — positioned top-right so it never overlaps the
// primary Jam controls (play / end / tempo) at the bottom of the screen.
export const DevPanel: React.FC<DevPanelProps> = ({
  onSimulateChord,
  onSimulateTransition,
  onRecovery,
  onStableFlow,
}) => {
  const [open, setOpen] = useState(false);
  const { t } = useI18n();

  const btn =
    "w-full rounded-lg px-3 py-2 text-left text-[11px] font-mono tracking-wide text-slate-300 transition hover:text-amber-200 hover:bg-white/5 active:scale-[0.98]";

  return (
    <div className="fixed right-3 top-20 z-40 flex flex-col items-end">
      <button
        data-testid="dev-control-panel-toggle"
        onClick={() => setOpen((o) => !o)}
        className="flex h-10 w-10 items-center justify-center rounded-full text-slate-400 transition hover:text-amber-300 active:scale-95"
        style={{ background: "rgba(18,21,25,0.9)", border: "1px solid rgba(255,255,255,0.12)" }}
        title="Developer simulation panel"
      >
        <FlaskConical size={17} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.96 }}
            transition={{ duration: 0.2 }}
            className="mt-2 w-56 rounded-2xl p-3"
            style={{
              background: "rgba(18,21,25,0.97)",
              border: "1px solid rgba(255,255,255,0.1)",
              backdropFilter: "blur(16px)",
            }}
          >
            <div className="mb-2 flex items-center justify-between">
              <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                {t("dev.title")}
              </span>
              <button onClick={() => setOpen(false)} className="text-slate-500 hover:text-slate-200">
                <X size={14} />
              </button>
            </div>
            <div className="space-y-0.5">
              <button data-testid="dev-simulate-chord-btn" className={btn} onClick={onSimulateChord}>
                {t("dev.nextSection")}
              </button>
              <button className={btn} onClick={onSimulateTransition}>
                {t("dev.upcoming")}
              </button>
              <button className={btn} onClick={onStableFlow}>
                {t("dev.stableFlow")}
              </button>
              <div className="my-1 h-px bg-white/10" />
              <button data-testid="dev-simulate-recovery-btn" className={btn} onClick={() => onRecovery(3)}>
                {t("dev.recovery1")}
              </button>
              <button className={btn} onClick={() => onRecovery(2)}>
                {t("dev.recovery2")}
              </button>
              <button className={btn} onClick={() => onRecovery(1)}>
                {t("dev.recovery3")}
              </button>
              <button className={btn} onClick={() => onRecovery(null)}>
                {t("dev.clearRecovery")}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
